import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Users, TrendingUp, AlertCircle, Plus, Search, RefreshCw, Building2, IndianRupee, FileText, Clock, Upload, Eye, ShoppingCart, BarChart3 } from 'lucide-react';

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

export const DistributorDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');

  // New client form state
  const [newClient, setNewClient] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    mobile: '',
    pan: '',
    date_of_birth: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    nomination_opt: 'N',
    nominee_name: '',
    nominee_relation: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/bse/dashboard/stats`),
        axios.get(`${API}/bse/clients?limit=50`),
        axios.get(`${API}/bse/orders?limit=50`)
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data.clients || []);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleNewClientSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newClient.first_name || !newClient.pan || !newClient.email || !newClient.mobile) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate PAN format
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panRegex.test(newClient.pan.toUpperCase())) {
      toast.error('Invalid PAN format. Example: ABCDE1234F');
      return;
    }

    try {
      const response = await axios.post(`${API}/bse/clients/register`, {
        ...newClient,
        pan: newClient.pan.toUpperCase()
      });
      
      if (response.data.status === 'success') {
        toast.success(`Client registered! UCC: ${response.data.ucc_code}`);
      } else {
        toast.success('Client saved locally. BSE registration pending.');
      }
      
      setShowNewClientDialog(false);
      setNewClient({
        first_name: '', middle_name: '', last_name: '', email: '', mobile: '',
        pan: '', date_of_birth: '', address_line1: '', address_line2: '',
        city: '', state: '', pincode: '', account_number: '', ifsc_code: '',
        bank_name: '', nomination_opt: 'N', nominee_name: '', nominee_relation: ''
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error registering client:', error);
      toast.error(error.response?.data?.detail || 'Failed to register client');
    }
  };

  const retryClientRegistration = async (clientId) => {
    try {
      const response = await axios.post(`${API}/bse/clients/${clientId}/retry-registration`);
      toast.success(`Registration successful! UCC: ${response.data.ucc_code}`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Retry failed');
    }
  };

  const filteredClients = clients.filter(client => 
    client.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.pan?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.ucc_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      registered: { variant: 'default', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
      pending: { variant: 'secondary', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      failed: { variant: 'destructive', className: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{status?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="distributor-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Distributor Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage your clients and transactions</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchDashboardData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(true)} data-testid="bulk-import-btn">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Register New Client (UCC Creation)</DialogTitle>
                  <DialogDescription>
                    Fill in client details for BSE StARMF registration
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleNewClientSubmit} className="space-y-4">
                  {/* Personal Details */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Personal Details</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          value={newClient.first_name}
                          onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                          required
                          data-testid="input-first-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="middle_name">Middle Name</Label>
                        <Input
                          id="middle_name"
                          value={newClient.middle_name}
                          onChange={(e) => setNewClient({...newClient, middle_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name">Last Name</Label>
                        <Input
                          id="last_name"
                          value={newClient.last_name}
                          onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="pan">PAN *</Label>
                        <Input
                          id="pan"
                          value={newClient.pan}
                          onChange={(e) => setNewClient({...newClient, pan: e.target.value.toUpperCase()})}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          required
                          data-testid="input-pan"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dob">Date of Birth *</Label>
                        <Input
                          id="dob"
                          value={newClient.date_of_birth}
                          onChange={(e) => setNewClient({...newClient, date_of_birth: e.target.value})}
                          placeholder="DD/MM/YYYY"
                          required
                          data-testid="input-dob"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                          required
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile">Mobile *</Label>
                        <Input
                          id="mobile"
                          value={newClient.mobile}
                          onChange={(e) => setNewClient({...newClient, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                          placeholder="10 digits"
                          required
                          data-testid="input-mobile"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Address</h3>
                    <div>
                      <Label htmlFor="address1">Address Line 1 *</Label>
                      <Input
                        id="address1"
                        value={newClient.address_line1}
                        onChange={(e) => setNewClient({...newClient, address_line1: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="address2">Address Line 2</Label>
                      <Input
                        id="address2"
                        value={newClient.address_line2}
                        onChange={(e) => setNewClient({...newClient, address_line2: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={newClient.city}
                          onChange={(e) => setNewClient({...newClient, city: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Select value={newClient.state} onValueChange={(v) => setNewClient({...newClient, state: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATES.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="pincode">Pincode *</Label>
                        <Input
                          id="pincode"
                          value={newClient.pincode}
                          onChange={(e) => setNewClient({...newClient, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Bank Details</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="account">Account Number *</Label>
                        <Input
                          id="account"
                          value={newClient.account_number}
                          onChange={(e) => setNewClient({...newClient, account_number: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="ifsc">IFSC Code *</Label>
                        <Input
                          id="ifsc"
                          value={newClient.ifsc_code}
                          onChange={(e) => setNewClient({...newClient, ifsc_code: e.target.value.toUpperCase()})}
                          placeholder="e.g., HDFC0000001"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bank">Bank Name *</Label>
                      <Input
                        id="bank"
                        value={newClient.bank_name}
                        onChange={(e) => setNewClient({...newClient, bank_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  {/* Nomination */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Nomination (Optional)</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Nomination</Label>
                        <Select value={newClient.nomination_opt} onValueChange={(v) => setNewClient({...newClient, nomination_opt: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Y">Yes</SelectItem>
                            <SelectItem value="N">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newClient.nomination_opt === 'Y' && (
                        <>
                          <div>
                            <Label htmlFor="nominee_name">Nominee Name</Label>
                            <Input
                              id="nominee_name"
                              value={newClient.nominee_name}
                              onChange={(e) => setNewClient({...newClient, nominee_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="nominee_relation">Relation</Label>
                            <Input
                              id="nominee_relation"
                              value={newClient.nominee_relation}
                              onChange={(e) => setNewClient({...newClient, nominee_relation: e.target.value})}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowNewClientDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-client-btn">
                      Register Client
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.clients?.total || 0}</p>
                  <p className="text-xs text-green-400 mt-1">{stats?.clients?.active || 0} active</p>
                </div>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total AUM</p>
                  <p className="text-3xl font-bold text-foreground">
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(stats?.aum?.total || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.orders?.total || 0}</p>
                  <p className="text-xs text-yellow-400 mt-1">{stats?.orders?.pending || 0} pending</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active SIPs</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.sips?.active || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="bg-card/50 backdrop-blur border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate('/distributor/transactions')}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="font-medium">Place Order</p>
                <p className="text-sm text-muted-foreground">Purchase or redeem funds</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-card/50 backdrop-blur border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate('/distributor/sip')}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="font-medium">Manage SIPs</p>
                <p className="text-sm text-muted-foreground">Register or cancel SIPs</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-card/50 backdrop-blur border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate('/distributor/reports')}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="font-medium">View Reports</p>
                <p className="text-sm text-muted-foreground">Analytics and insights</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
          <TabsList className="bg-card/50 border border-border/50">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
            <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Recent Clients */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Recent Clients
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {clients.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No clients yet. Add your first client!</p>
                  ) : (
                    <div className="space-y-3">
                      {clients.slice(0, 5).map(client => (
                        <div key={client.client_id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                          <div>
                            <p className="font-medium">{client.first_name} {client.last_name}</p>
                            <p className="text-xs text-muted-foreground">{client.pan} | {client.email}</p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(client.bse_registration_status)}
                            {client.ucc_code && (
                              <p className="text-xs text-muted-foreground mt-1">UCC: {client.ucc_code}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Orders */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Recent Orders
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No orders yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {orders.slice(0, 5).map(order => (
                        <div key={order.order_id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                          <div>
                            <p className="font-medium">{order.scheme_code}</p>
                            <p className="text-xs text-muted-foreground">{order.transaction_type} | {order.ucc_code}</p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(order.status)}
                            <p className="text-xs text-muted-foreground mt-1">
                              {order.amount ? `Rs. ${order.amount.toLocaleString()}` : `${order.units} units`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pending Actions */}
            {(stats?.clients?.pending > 0 || stats?.orders?.pending > 0) && (
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-yellow-400">
                    <AlertCircle className="w-5 h-5" />
                    Pending Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats?.clients?.pending > 0 && (
                      <p className="text-sm">{stats.clients.pending} client(s) pending BSE registration</p>
                    )}
                    {stats?.orders?.pending > 0 && (
                      <p className="text-sm">{stats.orders.pending} order(s) pending processing</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="clients" className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, PAN, email, or UCC..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="client-search"
                />
              </div>
            </div>

            {/* Clients Table */}
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border/50">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-4 font-medium">Client</th>
                        <th className="p-4 font-medium">PAN</th>
                        <th className="p-4 font-medium">Contact</th>
                        <th className="p-4 font-medium">UCC</th>
                        <th className="p-4 font-medium">Status</th>
                        <th className="p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-muted-foreground">
                            {searchQuery ? 'No clients match your search' : 'No clients registered yet'}
                          </td>
                        </tr>
                      ) : (
                        filteredClients.map(client => (
                          <tr key={client.client_id} className="border-b border-border/30 hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/distributor/client/${client.client_id}`)}>
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{client.first_name} {client.middle_name} {client.last_name}</p>
                                <p className="text-xs text-muted-foreground">{client.client_code}</p>
                              </div>
                            </td>
                            <td className="p-4 font-mono text-sm">{client.pan}</td>
                            <td className="p-4">
                              <p className="text-sm">{client.email}</p>
                              <p className="text-xs text-muted-foreground">{client.mobile}</p>
                            </td>
                            <td className="p-4 font-mono text-sm">{client.ucc_code || '-'}</td>
                            <td className="p-4">{getStatusBadge(client.bse_registration_status)}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/distributor/client/${client.client_id}`); }}
                                  data-testid={`view-${client.client_id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                {client.bse_registration_status === 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); retryClientRegistration(client.client_id); }}
                                    data-testid={`retry-${client.client_id}`}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Retry
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border/50">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-4 font-medium">Order ID</th>
                        <th className="p-4 font-medium">UCC</th>
                        <th className="p-4 font-medium">Type</th>
                        <th className="p-4 font-medium">Scheme</th>
                        <th className="p-4 font-medium">Amount/Units</th>
                        <th className="p-4 font-medium">Status</th>
                        <th className="p-4 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-muted-foreground">
                            No orders yet
                          </td>
                        </tr>
                      ) : (
                        orders.map(order => (
                          <tr key={order.order_id} className="border-b border-border/30 hover:bg-muted/20">
                            <td className="p-4 font-mono text-sm">{order.order_id}</td>
                            <td className="p-4 font-mono text-sm">{order.ucc_code}</td>
                            <td className="p-4">
                              <Badge variant={order.transaction_type === 'P' ? 'default' : 'secondary'}>
                                {order.transaction_type === 'P' ? 'Purchase' : 'Redemption'}
                              </Badge>
                            </td>
                            <td className="p-4 text-sm">{order.scheme_code}</td>
                            <td className="p-4 text-sm">
                              {order.amount ? `Rs. ${order.amount.toLocaleString()}` : `${order.units} units`}
                            </td>
                            <td className="p-4">{getStatusBadge(order.status)}</td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Import Clients</DialogTitle>
              <DialogDescription>Upload an Excel file with client details</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop your Excel file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  id="bulk-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      toast.info(`File selected: ${file.name}. Bulk import feature coming soon!`);
                    }
                  }}
                />
                <Button variant="outline" onClick={() => document.getElementById('bulk-upload')?.click()}>
                  Select File
                </Button>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Required columns:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• first_name, last_name, email, mobile</li>
                  <li>• pan, date_of_birth (DD/MM/YYYY)</li>
                  <li>• address_line1, city, state, pincode</li>
                  <li>• account_number, ifsc_code, bank_name</li>
                </ul>
                <Button variant="link" className="p-0 h-auto mt-2 text-xs" onClick={() => toast.info('Template download coming soon!')}>
                  Download Template
                </Button>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>
                Cancel
              </Button>
              <Button disabled>
                <Upload className="w-4 h-4 mr-2" />
                Import Clients
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DistributorDashboardPage;
